import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	Application,
	ApplicationCheck,
	ApplicationCheckInterface,
	ApplicationCheckMode,
	ConfigurationType,
	Tsa
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import TsaDialogFields from "../field/TsaDialogFields";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Dropdown} from "primereact/dropdown";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";
import DomHelper from "../../../shared/helper/DomHelper";
import {PortalTsa} from "../../../shared/models/PortalTsa";
import {InputSwitch} from "primereact/inputswitch";

export interface TsaDialogProperties extends AdminDialogProperties<PortalTsa> {
}

export interface TsaDialogStatus extends AdminDialogState<PortalTsa> {
	passwordChanged: boolean
}

export class TsaDialogDefinition extends AdminDialog<PortalTsa, TsaDialogProperties, TsaDialogStatus> {
	private passwordField: RefObject<Password>;

	constructor(props: Readonly<TsaDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			passwordChanged: false
		};

		this.passwordField = createRef<Password>();
	}

	public renderContents(): ReactNode {
		let parameter: PortalTsa | undefined = this.state.parameter;
		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let isEnabled: boolean = FunctionHelper.applyValue(parameter?.enabled, PortalTsa.getEnabledDefault());

		return (
			<fieldset className="p-col-12 p-grid">
				<div className=" p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-tsa-enabled">
						<Translate {...this.props} data="tsa.enabled" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-tsa-enabled" checked={isEnabled}
									 onChange={(e) => this.onChange(
										 TsaDialogFields.ENABLED, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!isEnabled}>
					<label className="p-col-12 p-sm-4" htmlFor="control-tsa-url">
						<Translate {...this.props} data="tsa.url" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalTsa.getUrlDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-tsa-url"
								   value={FunctionHelper.applyValue(parameter?.url, PortalTsa.getUrlDefault())}
								   onInput={(e) => this.onChange(
									   TsaDialogFields.URL, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!isEnabled}>
					<label className="p-col-12 p-sm-4" htmlFor="control-tsa-hashAlgorithm">
						<Translate {...this.props} data="tsa.hashAlgorithm.label" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalTsa.getHashAlgorithmDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Dropdown inputId="control-tsa-hashAlgorithm"
								  value={FunctionHelper.applyValue(
									  parameter?.hashAlgorithm, PortalTsa.getHashAlgorithmDefault()
								  )}
								  options={[
									  {
										  label: Translate.value("tsa.hashAlgorithm.MD5", {ns: "dialog"}),
										  value: "MD5"
									  },
									  {
										  label: Translate.value("tsa.hashAlgorithm.SHA1", {ns: "dialog"}),
										  value: "SHA-1"
									  },
									  {
										  label: Translate.value(
											  "tsa.hashAlgorithm.SHA224", {ns: "dialog"}
										  ),
										  value: "SHA-224"
									  },
									  {
										  label: Translate.value(
											  "tsa.hashAlgorithm.SHA256", {ns: "dialog"}
										  ),
										  value: "SHA-256"
									  },
									  {
										  label: Translate.value(
											  "tsa.hashAlgorithm.SHA384", {ns: "dialog"}
										  ),
										  value: "SHA-384"
									  },
									  {
										  label: Translate.value(
											  "tsa.hashAlgorithm.SHA512", {ns: "dialog"}
										  ),
										  value: "SHA-512"
									  }
								  ]}
								  onChange={(e) => this.onChange(
									  TsaDialogFields.HASHALGORITHM, e.value
								  )}
								  appendTo={document.getElementById("root")}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!isEnabled}>
					<label className="p-col-12 p-sm-4" htmlFor="control-tsa-user">
						<Translate {...this.props} data="tsa.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalTsa.getUserDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-tsa-user"
								   value={FunctionHelper.applyValue(parameter?.user, PortalTsa.getUserDefault())}
								   onInput={(e) => this.onChange(
									   TsaDialogFields.USER, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!isEnabled}>
					<label className="p-col-12 p-sm-4" htmlFor="control-tsa-password">
						<Translate {...this.props} data="tsa.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={PortalTsa.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password id="control-tsa-password" feedback={false} toggleMask={this.state.passwordChanged}
								  ref={this.passwordField} appendTo={document.getElementById("root")}
								  onFocus={() => {
									  if (this.state.passwordChanged) {
										  return;
									  }

									  this.onChange(TsaDialogFields.PASSWORD, "");

									  this.setState({
										  passwordChanged: true
									  });
								  }}
								  value={FunctionHelper.applyValue(parameter?.password, PortalTsa.getPasswordDefault())}
								  onInput={(e) => this.onChange(
									  TsaDialogFields.PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["tsa-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.tsa.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-clock"}/>
					<Translate {...this.props} data="tsa.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isValid(): boolean {
		let parameter: PortalTsa | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return false;
		}

		let result: boolean = super.isValid();

		if ((FunctionHelper.isEmpty(parameter?.password) && !FunctionHelper.isEmpty(parameter?.user)) ||
			(!FunctionHelper.isEmpty(parameter?.password) && FunctionHelper.isEmpty(parameter?.user))) {
			result = false;
		}

		return result;
	}

	protected onChange(field: TsaDialogFields, value: any): void {
		let parameter: PortalTsa | undefined = this.state.parameter;

		switch (field) {
			case TsaDialogFields.URL:
				parameter!.url = value;
				break;
			case TsaDialogFields.HASHALGORITHM:
				parameter!.hashAlgorithm = value;
				break;
			case TsaDialogFields.USER:
				parameter!.user = value;
				break;
			case TsaDialogFields.PASSWORD:
				parameter!.password = value;
				break;
			case TsaDialogFields.ENABLED:
				parameter!.enabled = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: PortalTsa | undefined = this.state.parameter;
		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();

		let checks: Array<ApplicationCheck> = [];
		if (parameter?.enabled) {
			applicationConfiguration.tsa = Tsa.fromJson(parameter?.toJson());
			checks.push(ApplicationCheck.fromJson({
				checkType: ApplicationCheckMode.Tsa
			} as ApplicationCheckInterface));
		} else {
			applicationConfiguration.tsa = undefined;
		}

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration, checks);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.tsa", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected async reset(): Promise<void> {
		await super.reset();

		if (FunctionHelper.exists(this.passwordField.current)) {
			let passwordContainer: HTMLDivElement = this.passwordField.current!.getElement();
			let type: string | null | undefined = DomHelper.find(passwordContainer, "input")?.getAttribute("type");

			if (type !== "password") {
				DomHelper.find(passwordContainer, "input")?.setAttribute("type", "password");
				DomHelper.find(passwordContainer, "i")?.setAttribute("class", "pi pi-eye");
			}
		}

		this.setState({
			passwordChanged: false
		});
	}

	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalTsa | undefined = this.state.parameter;
		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		if (!FunctionHelper.isEmpty(parameter?.url)) {
			parameter!.enabled = true;
		}

		this.setState({
			parameter: parameter?.clone(),
			originalParameter: parameter?.clone()
		});
	}

	protected hide() {
		this.setState({
			passwordChanged: false
		});

		super.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.TSA;
	}
}

const TsaDialog = React.memo(TsaDialogDefinition,
	(prevProps: Readonly<TsaDialogProperties>, nextProps: Readonly<TsaDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default TsaDialog;